import { useEffect, useId, useRef, type ReactElement, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import styles from './Modal.module.css';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled])';

export interface ModalProps {
  title: string;
  /** Texto curto abaixo do título (lido junto com ele ao abrir). */
  description?: string;
  onClose: () => void;
  /**
   * Há um pedido em andamento: `Esc` não fecha (a resposta chegaria a uma janela que já não existe e
   * a pessoa não saberia se o envio saiu).
   */
  busy?: boolean;
  children: ReactNode;
}

/**
 * Janela por cima da tela. `Esc` fecha; o foco fica preso dentro enquanto ela está aberta, começa no
 * campo marcado com `data-autofocus` (ou no primeiro) e volta ao botão que a abriu ao fechar. Clicar fora NÃO fecha: o que foi digitado
 * num formulário não se perde por um clique perdido. O conteúdo rola dentro dela (telas baixas).
 */
export function Modal({
  title,
  description,
  onClose,
  busy = false,
  children,
}: ModalProps): ReactElement {
  const titleId = useId();
  const descriptionId = useId();
  const dialog = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    // O campo marcado com `data-autofocus` abre com o foco; sem marca, o primeiro da janela.
    const start =
      dialog.current?.querySelector<HTMLElement>('[data-autofocus]') ??
      dialog.current?.querySelector<HTMLElement>(FOCUSABLE);
    start?.focus();
    return () => opener?.focus?.({ preventScroll: true });
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.defaultPrevented) return;
      if (event.key === 'Escape') {
        if (busy) return;
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(dialog.current?.querySelectorAll<HTMLElement>(FOCUSABLE) ?? []);
      const first = items[0];
      const last = items.at(-1);
      if (!first || !last) return;
      const active = document.activeElement;
      if (!dialog.current?.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose, busy]);

  return createPortal(
    <div className={styles.backdrop} role="presentation">
      <div
        ref={dialog}
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
      >
        <h2 id={titleId} className={styles.title}>
          {title}
        </h2>
        {description && (
          <p id={descriptionId} className={styles.description}>
            {description}
          </p>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}
